/* eslint-disable camelcase */
import {
  Button,
  Checkbox,
  Flex,
  Heading,
  HStack,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  TableContainer,
} from '@chakra-ui/react';
import {
  ColumnDef,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { finleyAssert } from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';
import {
  ValleyBankCollateralAccount,
  FinleyLoanParty,
  typeguards as valleyBankTypeguards,
} from '@finley-technologies/valley-bank-utils';

import {
  ValleyBankAddToAssignmentQueueGQLType,
  GetValleyBankAssignmentQueueQueryGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { getCustomerFromReceivable } from './shared';
import { Alert } from '~/packages/common-frontend/components/Alert';
import {
  CsvXlsxDownloadSelect,
  getTanstackVisibleDataForDownload,
} from '~/packages/common-frontend/components/CsvXlsxDownloadSelect';
import { Icon } from '~/packages/common-frontend/components/icons';
import { ColumnDisplaySettingsButton } from '~/packages/common-frontend/components/TanStack/ColumnReordering';
import {
  getColumnNames,
  createDefaultTableColumnConfiguration,
  TableColumnConfigurationKey,
  useTableColumnPreferences,
} from '~/packages/common-frontend/hooks/useTableColumnPreferences';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from '~/packages/common-frontend/utils';
import { generateAssignmentUrl } from '~/valleyBank/domains/Assignment/ui/Assignment';
import { CustomerSummaryLink } from '~/valleyBank/domains/Customers/hooks/CustomerContext';

const TABLE_KEY: TableColumnConfigurationKey =
  'valleyBankSbl.loanAccountAssignedCollateralAccounts';

const DEFAULT_COLUMN_CONFIG = createDefaultTableColumnConfiguration({
  tableKey: TABLE_KEY,
  columns: [
    {
      columnKey: 'accountNumber',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'productName',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'customer',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'loanPartyType',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'marketValue',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'collateralAccountEffectiveAdvanceRate',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'preAllocationLendableValue',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'effectiveAllocation',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'postAllocationLendableValue',
      isVisible: true,
      isFixed: false,
    },
    {
      columnKey: 'cifNum',
      isVisible: true,
      isFixed: false,
    },
  ],
});

const generateColumnDefs = ({
  loanParties,
}: {
  loanParties: any;
}): ColumnDef<ValleyBankCollateralAccount, any>[] => [
  {
    id: 'accountNumber',
    accessorKey: 'accountNumber',
    header: 'Account Number',
  },
  {
    id: 'productName',
    accessorKey: 'productName',
    header: 'Product Name',
  },
  {
    id: 'cifNum',
    accessorKey: 'cifNum',
    header: 'CIF Number',
    cell: function formatCifNum({ getValue }) {
      return (
        <CustomerSummaryLink cifnum={getValue()}>
          {getValue()}
        </CustomerSummaryLink>
      );
    },
  },
  {
    id: 'customer',
    header: 'Customer',
    accessorFn: (row) => {
      const customer = getCustomerFromReceivable(row.receivables, loanParties);

      return customer?.full_name || row.owner?.full_name || '-';
    },
    cell: function formatCustomer({
      getValue,
      row: {
        original: { cifNum },
      },
    }) {
      return (
        <CustomerSummaryLink cifnum={cifNum}>{getValue()}</CustomerSummaryLink>
      );
    },
  },
  {
    id: 'loanPartyType',
    header: 'Loan Party Type',
    accessorFn: (row) => {
      const customer = getCustomerFromReceivable(row.receivables, loanParties);

      return customer?.rel_code_description;
    },
    cell: function formatLoanPartyType({ getValue }) {
      return (
        getValue() || (
          <HStack w="100%" spacing="8px">
            <Text>N/A</Text>
            <Tooltip label="Not a loan party of this loan" borderRadius="8px">
              <span
                style={{
                  padding: '16px 4px',
                  verticalAlign: 'middle',
                }}
              >
                <Icon icon="circle-info" />
              </span>
            </Tooltip>
          </HStack>
        )
      );
    },
  },
  {
    id: 'marketValue',
    accessorKey: 'marketValue',
    header: 'Collateral Market Value',
    cell: function formatMarketValue({ getValue }) {
      return formatCurrencyAmount(getValue(), Bank.Currency.USD);
    },
  },
  {
    id: 'collateralAccountEffectiveAdvanceRate',
    accessorKey: 'effectiveAdvanceRate',
    header: 'Blended Advance Rate',
    cell: function formatCollateralAccountEffectiveAdvanceRate({ getValue }) {
      return percentageToPercentageLabel(getValue() * 100, 2);
    },
  },
  {
    id: 'preAllocationLendableValue',
    accessorKey: 'preAllocationLendableValue',
    header: 'Pre Allocation Lendable Value',
    cell: function formatPreAllocationLendableValue({ getValue }) {
      return formatCurrencyAmount(getValue(), Bank.Currency.USD);
    },
  },
  {
    id: 'effectiveAllocation',
    accessorKey: 'effectiveAllocation',
    header: 'Blended Allocation',
    cell: function formatEffectiveAllocation({ getValue }) {
      return percentageToPercentageLabel(getValue() * 100, 2);
    },
  },
  {
    id: 'postAllocationLendableValue',
    accessorKey: 'postAllocationLendableValue',
    header: 'Post Allocation Lendable Value',
    cell: function formatPostAllocationLendableValue({ getValue }) {
      return formatCurrencyAmount(getValue(), Bank.Currency.USD);
    },
  },
];

type AssignedCollateralAccountsProps = {
  collateralAccounts: ValleyBankCollateralAccount[];
  loanAccountNumber: string;
  canUnassign: boolean;
  loanParties: FinleyLoanParty[];
  isLoading: boolean;
  isError: boolean;
  queuedSelectedCollateralAccounts: Set<string> | null;
  assignmentQueueData: GetValleyBankAssignmentQueueQueryGQLType;
  toggleCollateralSelect: (params: {
    collateralAccountNumber: string;
    isChecked: boolean;
  }) => void;
  setUnassignment: (params: ValleyBankAddToAssignmentQueueGQLType) => void;
  userId: string;
};

export const AssignedCollateralAccounts = ({
  collateralAccounts,
  loanAccountNumber,
  canUnassign,
  loanParties,
  isLoading,
  isError,
  queuedSelectedCollateralAccounts,
  assignmentQueueData,
  toggleCollateralSelect,
  setUnassignment,
  userId,
}: AssignedCollateralAccountsProps) => {
  const navigate = useNavigate();

  const {
    columnConfiguration,
    columnOrder,
    columnVisibility,
    updateUserColumnConfiguration,
  } = useTableColumnPreferences({
    tableKey: TABLE_KEY,
    defaultTableColumnConfiguration: DEFAULT_COLUMN_CONFIG,
  });

  const { columnDefs, columnNames } = useMemo(() => {
    const _columnDefs = generateColumnDefs({ loanParties });
    return {
      columnDefs: _columnDefs,
      columnNames: getColumnNames(_columnDefs),
    };
  }, [loanParties]);

  const table = useReactTable({
    data: collateralAccounts,
    columns: columnDefs,
    state: {
      columnOrder,
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
  });

  const csvDownloadData = getTanstackVisibleDataForDownload({
    tableInstance: table,
  });

  return (
    <>
      <Flex alignItems="center" mt="64px" w="100%" justify="space-between">
        <Heading as="h2" fontSize="24px">
          Assigned Collateral Accounts
        </Heading>
        {collateralAccounts.length > 0 && (
          <HStack alignSelf="flex-end" spacing="1em">
            <ColumnDisplaySettingsButton
              initialColumns={columnConfiguration}
              updateColumnConfigs={updateUserColumnConfiguration}
              columnNames={columnNames}
            />
            <CsvXlsxDownloadSelect
              dataRows={csvDownloadData.dataRows}
              headers={csvDownloadData.headers}
              filenamePrefix={`collateral-accounts-loan-account-${loanAccountNumber}`}
            />
          </HStack>
        )}
      </Flex>
      {collateralAccounts.length === 0 && (
        <Alert status="info">
          <Text>No collateral accounts associated with this Loan Account</Text>
        </Alert>
      )}
      <TableContainer
        mt="32px"
        // maxH required for table top sticky header to work
        // since position:sticky attaches to the nearest ancestor with a scrolling mechanism
        // https://stackoverflow.com/questions/70770027/table-with-sticky-header-and-horizontal-scroll
        overflowX="auto"
        overflowY="auto"
        position="relative"
        id="receivables-dashboard-main-table"
        w="100%"
      >
        <Table id="valley-bank-customer-collateral-accounts-table">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {canUnassign && <Th />}
                {headerGroup.headers.map((header) => {
                  return (
                    <Th key={header.id}>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </Th>
                  );
                })}
                {canUnassign && <Th />}
              </Tr>
            ))}
          </Thead>
          {isLoading && !isError && (
            <Tbody>
              {Array.from(Array(10).keys()).map((i) => (
                <Tr key={i}>
                  {canUnassign && (
                    <Td>
                      <Skeleton h="100%" />
                    </Td>
                  )}
                  {table.getVisibleFlatColumns().map((column) => (
                    <Td
                      key={`${column.id}+${i}`}
                      padding="8px 16px"
                      height="56px"
                    >
                      <Skeleton
                        h="100%"
                        w={
                          column.columnDef.header === 'Borrower'
                            ? '400px'
                            : undefined
                        }
                      />
                    </Td>
                  ))}
                  {canUnassign && (
                    <Td>
                      <Skeleton h="100%" />
                    </Td>
                  )}
                </Tr>
              ))}
            </Tbody>
          )}
          {!isLoading && !isError && (
            <Tbody>
              {table.getRowModel().rows.map((row) => {
                const isCollateralSelected = !!queuedSelectedCollateralAccounts?.has(
                  row.original.accountNumber,
                );
                const baseFields =
                  row.original.receivables[0].receivableReponseTapeRow
                    .baseFields;
                finleyAssert(
                  valleyBankTypeguards.hasValleyBankRequiredBaseFields(
                    baseFields,
                  ),
                  'Must be valid required base fields.',
                );
                const collateralAccountType =
                  baseFields.collateral_account_type;
                const duplicateRecord = assignmentQueueData.valleyBankAssignmentQueue.find(
                  (q) =>
                    q.collateralAccountNumber === row.original.accountNumber &&
                    q.loanAccountNumber === loanAccountNumber &&
                    q.collateralAccountType === collateralAccountType &&
                    q.action === 'unassign',
                );

                return (
                  <Tr key={row.id}>
                    {canUnassign && (
                      <Td>
                        <Checkbox
                          defaultChecked={isCollateralSelected}
                          onChange={(e) => {
                            toggleCollateralSelect({
                              collateralAccountNumber:
                                row.original.accountNumber,
                              isChecked: e.target.checked,
                            });
                          }}
                        />
                      </Td>
                    )}
                    {row.getVisibleCells().map((cell) => (
                      <Td
                        key={cell.id}
                        textAlign={cell.column.columnDef.meta?.style?.textAlign}
                        padding={cell.column.columnDef.meta?.style?.padding}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Td>
                    ))}
                    {canUnassign && (
                      <Td>
                        {duplicateRecord ? (
                          <Button
                            colorScheme="aurora"
                            onClick={() => {
                              navigate(generateAssignmentUrl(true));
                            }}
                          >
                            View Queue
                          </Button>
                        ) : (
                          <Button
                            colorScheme="aurora"
                            onClick={() => {
                              setUnassignment({
                                loanAccountNumber,
                                collateralAccountNumber:
                                  row.original.accountNumber,
                                collateralAccountType,
                                action: 'unassign',
                                userId,
                              });
                            }}
                          >
                            Unassign
                          </Button>
                        )}
                      </Td>
                    )}
                  </Tr>
                );
              })}
            </Tbody>
          )}
        </Table>
      </TableContainer>
    </>
  );
};
